import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/ui/TextInput";
import Button from "../../../components/ui/Button";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import ExcerptsCard from "./ExcerptsCard";
import { updateBrandVoice, BrandVoice } from "../../../api/brandVoice";
import { useNotification } from "../../../context/NotificationContext";
import styles from "./EditBrandVoiceModal.module.css";

interface EditBrandVoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    brandVoice: BrandVoice;
}

const EditBrandVoiceModal: React.FC<EditBrandVoiceModalProps> = ({ isOpen, onClose, brandVoice }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [name, setName] = useState(brandVoice.name);
    const [description, setDescription] = useState(brandVoice.description || "");
    const [excerpts, setExcerpts] = useState<string[]>(brandVoice.excerpts || []);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setName(brandVoice.name);
        setDescription(brandVoice.description || "");
        setExcerpts(brandVoice.excerpts || []);
    }, [brandVoice]);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, handleOutsideClick]);

    const handleSave = async () => {
        // Validation
        if (!name.trim()) {
            showNotification({
                message: t("brandVoices.missingName"),
                type: "error",
            });
            return;
        }

        if (description.length > 1000) {
            showNotification({
                message: t("brandVoices.descriptionTooLong") || "Description must be 1000 characters or less",
                type: "error",
            });
            return;
        }

        if (excerpts.length > 3) {
            showNotification({
                message: t("brandVoices.tooManyExcerpts") || "Maximum 3 excerpts allowed",
                type: "error",
            });
            return;
        }

        const hasInvalidExcerpt = excerpts.some((excerpt) => excerpt.length > 400);
        if (hasInvalidExcerpt) {
            showNotification({
                message: t("brandVoices.excerptTooLong") || "Each excerpt must be 400 characters or less",
                type: "error",
            });
            return;
        }

        setIsSaving(true);

        try {
            await updateBrandVoice({
                brandVoiceId: brandVoice.id,
                name: name.trim(),
                description: description.trim() || undefined,
                excerpts: excerpts.length > 0 ? excerpts : undefined,
            });

            showNotification({
                message: t("brandVoices.updatedSuccessfully") || "Brand voice updated successfully",
                type: "success",
            });

            onClose();
        } catch (error: any) {
            console.error("Error updating brand voice:", error);
            let errorMessage = t("brandVoices.updateError") || "Failed to update brand voice";

            if (error.message) {
                if (error.message.includes("400")) {
                    errorMessage = t("brandVoices.invalidData") || "Invalid data provided";
                } else if (error.message.includes("404")) {
                    errorMessage = t("brandVoices.notFound") || "Brand voice not found";
                } else {
                    errorMessage = error.message;
                }
            }

            showNotification({
                message: errorMessage,
                type: "error",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteExcerpt = async (index: number) => {
        try {
            const result = await updateBrandVoice({
                brandVoiceId: brandVoice.id,
                deleteExcerptIndex: index,
            });

            // Update local state from API response
            setExcerpts(result.excerpts || []);

            showNotification({
                message: t("brandVoices.excerptDeleted") || "Excerpt deleted successfully",
                type: "success",
            });
        } catch (error: any) {
            console.error("Error deleting excerpt:", error);
            showNotification({
                message: t("brandVoices.deleteExcerptError") || "Failed to delete excerpt",
                type: "error",
            });
        }
    };

    const handleExcerptsChange = (updatedExcerpts: string[]) => {
        setExcerpts(updatedExcerpts);
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalContentRef}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t("brandVoices.editTitle") || "Edit Brand Voice"}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.section}>
                        <label className={styles.label}>{t("brandVoices.nameLabel")}</label>
                        <TextInput placeholder={t("name")} value={name} onChange={(e) => setName(e.target.value)} />
                    </div>

                    <div className={styles.section}>
                        <label className={styles.label}>{t("brandVoices.descriptionLabel")}</label>
                        <textarea
                            className={styles.descriptionTextarea}
                            placeholder={t("description")}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={6}
                        />
                        <div className={styles.characterCount}>{description.length}/1000</div>
                    </div>

                    <div className={styles.section}>
                        <ExcerptsCard
                            excerpts={excerpts}
                            onExcerptsChange={handleExcerptsChange}
                            onDeleteExcerpt={handleDeleteExcerpt}
                            editable={true}
                        />
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    {isSaving ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                            <Button variant="primary" label={t("save")} onClick={handleSave} />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default EditBrandVoiceModal;
